import { infiniteQueryOptions } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';

import {
  MarketplaceBrowseDocument,
  MarketplaceBrowseMapDocument,
  type MarketplaceBrowseMapQuery,
  type MarketplaceBrowseMapQueryVariables,
  type MarketplaceBrowseQuery,
  type MarketplaceBrowseQueryVariables,
} from './__generated__/marketplace-browse.zeus';

type MarketplaceMapMarkersResult = Extract<
  MarketplaceBrowseMapQuery['marketplace']['mapSearch'],
  { __typename: 'MarketplaceMapSearchMarkersResult' }
>;

export type MarketplaceBrowseMapMarkers = MarketplaceMapMarkersResult['markers'];

type MarketplaceBrowseListings = MarketplaceBrowseQuery['marketplace']['listings'];

type MarketplaceInitialBrowsePage = {
  kind: 'initial';
  listings: MarketplaceBrowseListings;
  mapMarkers: MarketplaceBrowseMapMarkers | null;
};

type MarketplacePaginationPage = {
  kind: 'pagination';
  listings: MarketplaceBrowseListings;
};

type MarketplaceBrowsePage = MarketplaceInitialBrowsePage | MarketplacePaginationPage;

type MarketplaceBrowseRequest = typeof requestZeusFromBrowser;

type MarketplaceBrowseRequestOptions = {
  /** Abort signal owned by the active TanStack query. */
  signal: AbortSignal;
  /** Search inputs shared by the list and map projections. */
  variables: MarketplaceBrowseVariables;
  /** Typed graph request implementation, replaceable by tests and SSR. */
  request: MarketplaceBrowseRequest;
};

type MarketplacePaginationRequestOptions = MarketplaceBrowseRequestOptions & {
  /** Zero-based listing page requested by TanStack Query. */
  page: number;
};

type MarketplaceBrowseQueryOptions = {
  /** Search inputs included in the TanStack query key. */
  variables: MarketplaceBrowseVariables;
  /** Optional request implementation for SSR and tests. */
  request?: MarketplaceBrowseRequest;
};

export type MarketplaceBrowseVariables = {
  listInput: MarketplaceBrowseQueryVariables['input'];
  mapInput?: MarketplaceBrowseMapQueryVariables['input'];
};

export const marketplaceBrowseQueryKey = ['marketplace', 'browse'] as const;

const selectMapMarkers = (result: MarketplaceBrowseMapQuery): MarketplaceBrowseMapMarkers => {
  const mapSearch = result.marketplace.mapSearch;
  if (mapSearch.__typename !== 'MarketplaceMapSearchMarkersResult') {
    throw new Error('Marketplace map search returned an unexpected result');
  }
  return mapSearch.markers;
};

const fetchInitialBrowse = async ({
  variables,
  signal,
  request,
}: MarketplaceBrowseRequestOptions): Promise<MarketplaceInitialBrowsePage> => {
  const browsePromise = request(MarketplaceBrowseDocument, { input: variables.listInput }, signal);
  const mapPromise = variables.mapInput
    ? request(MarketplaceBrowseMapDocument, { input: variables.mapInput }, signal)
    : undefined;
  const [browse, map] = await Promise.all([browsePromise, mapPromise]);

  return {
    kind: 'initial',
    listings: browse.marketplace.listings,
    mapMarkers: map ? selectMapMarkers(map) : null,
  };
};

const fetchListingsPage = async ({
  variables,
  page,
  signal,
  request,
}: MarketplacePaginationRequestOptions): Promise<MarketplacePaginationPage> => {
  const data = await request(
    MarketplaceBrowseDocument,
    { input: { ...variables.listInput, page } },
    signal,
  );

  return { kind: 'pagination', listings: data.marketplace.listings };
};

export const marketplaceBrowseQueryOptions = ({
  variables,
  request = requestZeusFromBrowser,
}: MarketplaceBrowseQueryOptions) =>
  infiniteQueryOptions({
    queryKey: [...marketplaceBrowseQueryKey, variables],
    queryFn: ({ pageParam, signal }): Promise<MarketplaceBrowsePage> =>
      pageParam === 0
        ? fetchInitialBrowse({ variables, signal, request })
        : fetchListingsPage({ variables, page: pageParam, signal, request }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.listings.nextPage ?? undefined,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    gcTime: 30_000,
    staleTime: 30_000,
  });
